/**
 * API Client Utilities
 * Wrapper for fetch with auth header injection, retry logic, and consistent error handling
 * 
 * Features:
 * - Auth token injection
 * - Retry-on-error behavior (iOS race condition fix)
 * - API event logging for diagnostics
 * - Consistent error handling
 * - Session expiry detection and event dispatch
 */

import { BACKEND_URL, STORAGE_KEYS } from '../config/appConfig';
import { logApiEvent, safeGetItem, safeRemoveItem } from './iosCompat';

/**
 * API Configuration
 */
const API_CONFIG = {
  maxRetries: 2,
  retryDelay: 500
};

/**
 * Session expiry event - dispatched when auth fails
 * Components can listen for this to show session expired modal
 */
export const SESSION_EXPIRED_EVENT = 'smartplaite:session_expired';

/**
 * Dispatch session expired event
 */
const dispatchSessionExpired = (reason = 'unknown') => {
  console.log('[API] Dispatching session expired event:', reason);
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { 
    detail: { reason, timestamp: Date.now() } 
  }));
};

/**
 * Get the current auth token from localStorage
 * Uses safe getter for iOS compatibility
 * @returns {string|null} Session token or null
 */
export const getAuthToken = () => {
  return safeGetItem(STORAGE_KEYS.SESSION_TOKEN);
};

/**
 * Check if we have a valid auth token
 * @returns {boolean}
 */
export const hasAuthToken = () => {
  const token = getAuthToken();
  return !!token && token.length > 0;
};

/**
 * Clear auth token (on logout or session expiry)
 */
export const clearAuthToken = () => {
  safeRemoveItem(STORAGE_KEYS.SESSION_TOKEN);
  safeRemoveItem('calorie_tracker_user_id');
};

/**
 * Make an authenticated API request with retry logic
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Fetch options
 * @param {number} retryCount - Current retry count (internal use)
 * @param {boolean} skipAuthCheck - Skip 401 handling (for auth endpoints)
 * @returns {Promise<{ok: boolean, data: any, error: string|null, status: number}>}
 */
export const apiRequest = async (endpoint, options = {}, retryCount = 0, skipAuthCheck = false) => {
  const url = `${BACKEND_URL}${endpoint}`;
  const token = getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  
  // Add auth header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    // Log API event for diagnostics
    logApiEvent({
      endpoint,
      method: options.method || 'GET',
      status: response.status
    });
    
    // Handle 401/403 - Session expired or invalid
    if (!skipAuthCheck && (response.status === 401 || response.status === 403)) {
      // Don't retry auth errors - session is invalid
      console.log('[API] Auth error on', endpoint, '- status:', response.status);
      
      // Only dispatch session expired if we had a token (not for login attempts)
      if (token) {
        dispatchSessionExpired(response.status === 401 ? 'token_invalid' : 'forbidden');
      }
      
      const data = await response.json().catch(() => ({}));
      return { 
        ok: false, 
        data, 
        error: data?.detail || 'Session expired. Please log in again.', 
        status: response.status,
        isAuthError: true
      };
    }
    
    // Try to parse JSON response
    let data = null;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    }
    
    if (response.ok) {
      return { ok: true, data, error: null, status: response.status };
    }
    
    // Retry on 5xx errors
    if (response.status >= 500 && retryCount < API_CONFIG.maxRetries) {
      console.log(`[API] ${endpoint} returned ${response.status}, retrying (${retryCount + 1}/${API_CONFIG.maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, API_CONFIG.retryDelay));
      return apiRequest(endpoint, options, retryCount + 1, skipAuthCheck);
    }
    
    const errorMessage = data?.detail || data?.message || `Request failed with status ${response.status}`;
    return { ok: false, data, error: errorMessage, status: response.status };
  } catch (error) {
    console.error(`[API] Request to ${endpoint} failed:`, error);
    
    // Log error for diagnostics
    logApiEvent({
      endpoint,
      method: options.method || 'GET',
      status: 0,
      error: error.message
    });
    
    // Retry on network error
    if (retryCount < API_CONFIG.maxRetries) {
      console.log(`[API] Network error on ${endpoint}, retrying (${retryCount + 1}/${API_CONFIG.maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, API_CONFIG.retryDelay));
      return apiRequest(endpoint, options, retryCount + 1, skipAuthCheck);
    }
    
    return { ok: false, data: null, error: error.message || 'Network error', status: 0 };
  }
};

/**
 * GET request helper
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Additional options
 * @returns {Promise<{ok: boolean, data: any, error: string|null, status: number}>}
 */
export const apiGet = (endpoint, options = {}) => {
  return apiRequest(endpoint, { method: 'GET', ...options });
};

/**
 * POST request helper
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} options - Additional options
 * @returns {Promise<{ok: boolean, data: any, error: string|null, status: number}>}
 */
export const apiPost = (endpoint, body, options = {}) => {
  return apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
    ...options
  });
};

/**
 * PUT request helper
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} options - Additional options
 * @returns {Promise<{ok: boolean, data: any, error: string|null, status: number}>}
 */
export const apiPut = (endpoint, body, options = {}) => {
  return apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
    ...options
  });
};

/**
 * DELETE request helper
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Additional options
 * @returns {Promise<{ok: boolean, data: any, error: string|null, status: number}>}
 */
export const apiDelete = (endpoint, options = {}) => {
  return apiRequest(endpoint, { method: 'DELETE', ...options });
};

/**
 * POST request with FormData (for file uploads)
 * @param {string} endpoint - API endpoint
 * @param {FormData} formData - Form data with files
 * @returns {Promise<{ok: boolean, data: any, error: string|null, status: number}>}
 */
export const apiPostFormData = async (endpoint, formData) => {
  const url = `${BACKEND_URL}${endpoint}`;
  const token = getAuthToken();
  
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Note: Don't set Content-Type for FormData - browser will set it with boundary
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    // Log API event
    logApiEvent({
      endpoint,
      method: 'POST',
      status: response.status
    });
    
    // Handle auth errors
    if (response.status === 401 || response.status === 403) {
      if (token) {
        dispatchSessionExpired(response.status === 401 ? 'token_invalid' : 'forbidden');
      }
      const data = await response.json().catch(() => ({}));
      return { 
        ok: false, 
        data, 
        error: data?.detail || 'Session expired', 
        status: response.status,
        isAuthError: true
      };
    }
    
    let data = null;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    }
    
    if (response.ok) {
      return { ok: true, data, error: null, status: response.status };
    } else {
      const errorMessage = data?.detail || data?.message || `Request failed with status ${response.status}`;
      return { ok: false, data, error: errorMessage, status: response.status };
    }
  } catch (error) {
    console.error(`[API] FormData request to ${endpoint} failed:`, error);
    logApiEvent({
      endpoint,
      method: 'POST',
      status: 0,
      error: error.message
    });
    return { ok: false, data: null, error: error.message || 'Network error', status: 0 };
  }
};

/**
 * Auth-specific request (skips session expiry handling)
 * Use for login, signup, password reset, etc.
 */
export const apiAuthRequest = (endpoint, options = {}) => {
  return apiRequest(endpoint, options, 0, true);
};

